/**
 * RulesModal - 规则弹窗组件
 *
 * 展示活动规则的弹窗
 *
 * @module RulesModal
 * @date 2026-01-04
 */

'use client'

import React from 'react'
import { BaseModal } from './BaseModal.js'

export interface RuleItem {
  /**
   * 规则项内容 (简单格式)
   */
  item?: string

  /**
   * 规则标题 (详细格式)
   */
  title?: string

  /**
   * 规则内容 (详细格式，支持字符串或数组)
   */
  content?: string | string[]
}

export interface RulesModalProps {
  /**
   * 是否显示弹窗
   */
  isOpen: boolean

  /**
   * 关闭弹窗回调
   */
  onClose: () => void

  /**
   * 弹窗标题
   * @default "Rules"
   */
  title?: string

  /**
   * 规则列表
   */
  rules: RuleItem[]

  /**
   * 自定义类名
   */
  className?: string

  /**
   * 主题模式
   * @default 'light'
   */
  theme?: 'light' | 'dark'
}

/**
 * RulesModal 规则弹窗组件
 */
export const RulesModal: React.FC<RulesModalProps> = ({
  isOpen,
  onClose,
  title = 'Rules:',
  rules,
  className,
  theme = 'light',
}) => {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-[480px] laptop:max-w-[520px]"
      background="bg-white"
      className={className}
      title={title}
      ariaLabelledBy="rules-modal-title"
      theme={theme}
    >
      <div className="laptop:px-8 p-4 pb-0">
        {/* 规则列表 */}
        <div className="max-h-[584px] overflow-y-auto pb-4">
          {rules.map((rule, index) => {
            // 支持两种格式:
            // 1. 简单格式: { item: string }
            // 2. 详细格式: { title: string, content: string | string[] }
            if (rule.item) {
              // 简单格式 (向后兼容)
              return (
                <div key={index} className="mb-2 text-[16px] font-[700] text-[#1D1D1F]">
                  <p className="m-0">{rule.item}</p>
                </div>
              )
            } else if (rule.title) {
              // 详细格式
              const contentArray = Array.isArray(rule.content) ? rule.content : rule.content ? [rule.content] : []

              return (
                <div key={index} className="mb-3">
                  {/* 规则标题 */}
                  <p className="m-0 mb-1 text-[16px] font-[700] text-[#1D1D1F]">{rule.title}</p>
                  {/* 规则内容列表 */}
                  {contentArray.length > 0 && (
                    <ul className="m-0 list-inside list-disc pl-4">
                      {contentArray.map((item, idx) => (
                        <li key={idx} className="text-[14px] font-[400] text-[#86868B]">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            }
            return null
          })}
        </div>
      </div>
    </BaseModal>
  )
}

export default RulesModal
